import { useAtom } from 'jotai'
import { useCallback, useEffect, useState } from 'react'

import { sessionAtom } from '@/shared-state/session'

export const useVersionCheck = () => {
  const [session, setSession] = useAtom(sessionAtom)
  const [showUpdateAlert, setShowUpdateAlert] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [lastCheckTime, setLastCheckTime] = useState(0)

  const checkVersion = useCallback(async () => {
    const now = Date.now()
    const timeSinceLastCheck = now - lastCheckTime

    // Throttle: only check if more than 60 seconds have passed
    if (timeSinceLastCheck < 60000) {
      return
    }

    setLastCheckTime(now)
    setIsChecking(true)

    try {
      // Get current version from package.json (this will be the client version)
      const currentVersion = '1.0.46' // You could also read this from package.json

      // Only fetch from /version in production, skip in development
      let latestVersion
      if (process.env.NODE_ENV === 'development') {
        // In development, use the current version (no version checking)
        latestVersion = currentVersion
      } else {
        // In production, fetch from Cloudflare function
        try {
          const response = await fetch('/version', { cache: 'no-store' })
          latestVersion = await response.text()
        } catch (error) {
          // Fallback to current version if fetch fails
          latestVersion = currentVersion
        }
      }

      if (latestVersion && latestVersion !== currentVersion) {
        // Check if the latest version is newer than what we have in session
        const sessionVersion = session?.version || currentVersion

        if (isVersionNewer(latestVersion, sessionVersion)) {
          setShowUpdateAlert(true)
          // Update session with the new version
          setSession(prev => ({
            ...prev,
            version: latestVersion
          }))
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to check version:', error)
    } finally {
      setIsChecking(false)
    }
  }, [session, setSession, lastCheckTime])

  const dismissAlert = () => {
    setShowUpdateAlert(false)
  }

  const reloadPage = () => {
    window.location.reload()
  }

  // Check version on mount and when window becomes active
  useEffect(() => {
    // Check immediately on mount
    checkVersion()

    // Listen for window focus events
    const handleFocus = () => {
      checkVersion()
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [checkVersion])

  return {
    showUpdateAlert,
    isChecking,
    dismissAlert,
    reloadPage,
    checkVersion
  }
}

// Helper function to compare semantic versions
function isVersionNewer(version1: string, version2: string): boolean {
  const v1Parts = version1.split('.').map(Number)
  const v2Parts = version2.split('.').map(Number)

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const v1Part = v1Parts[i] || 0
    const v2Part = v2Parts[i] || 0

    if (v1Part > v2Part) return true
    if (v1Part < v2Part) return false
  }

  return false
}
